import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import './CheckboxMultiSelect.css';

const CheckboxMultiSelect = ({
  items = [],
  selectedItems = [],
  onChange = () => {},
  withGrouping = false,
  itemRenderer: ItemRenderer = null,
  itemHeight = 40,
  showSearch = true,
  showSelectAll = true,
  showSelectedItems = true,
}) => {
  const [search, setSearch] = useState('');

  const selectedIds = useMemo(
    () => new Set(selectedItems.map((s) => s.id)),
    [selectedItems],
  );

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const term = search.toLowerCase();
    return items.filter((item) => item.label.toLowerCase().includes(term));
  }, [items, search]);

  // Build flat list of entries (group headers + items) for the left panel
  const displayEntries = useMemo(() => {
    if (!withGrouping) return filteredItems.map((item) => ({ type: 'item', item }));
    const groups = {};
    filteredItems.forEach((item) => {
      const g = item.group || '';
      if (!groups[g]) groups[g] = [];
      groups[g].push(item);
    });
    const entries = [];
    Object.keys(groups).sort().forEach((group) => {
      entries.push({ type: 'group', label: group, item: { label: group } });
      groups[group].forEach((item) => entries.push({ type: 'item', item }));
    });
    return entries;
  }, [filteredItems, withGrouping]);

  const enabledFilteredItems = useMemo(
    () => filteredItems.filter((item) => !item.disabled),
    [filteredItems],
  );

  const allFilteredSelected = enabledFilteredItems.length > 0 && enabledFilteredItems.every((item) => selectedIds.has(item.id));

  const toggleItem = (item) => {
    if (item.disabled) return;
    if (selectedIds.has(item.id)) {
      onChange(selectedItems.filter((s) => s.id !== item.id));
    } else {
      onChange([...selectedItems, item]);
    }
  };

  const toggleAll = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(enabledFilteredItems.map((i) => i.id));
      onChange(selectedItems.filter((s) => !filteredIds.has(s.id)));
    } else {
      const merged = [...selectedItems];
      enabledFilteredItems.forEach((item) => {
        if (!selectedIds.has(item.id)) merged.push(item);
      });
      onChange(merged);
    }
  };

  const removeItem = (item) => {
    onChange(selectedItems.filter((s) => s.id !== item.id));
  };

  const clearAll = () => {
    onChange([]);
  };

  return (
    <div className="kn-multi-select-wrapper">
      {/* Left panel - available items */}
      <div className="kn-column">
        {showSearch && (
          <div className="kn-search">
            <input
              type="text"
              className="kn-search-input"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="kn-search-icon">
              <svg fill="currentColor" height="1em" width="1em" viewBox="0 0 40 40">
                <path d="m15.9 23.4c4.1 0 7.5-3.4 7.5-7.5s-3.4-7.5-7.5-7.5-7.5 3.3-7.5 7.5 3.3 7.5 7.5 7.5z m10 0l8.2 8.2-2.5 2.5-8.2-8.2v-1.4l-0.5-0.4c-1.9 1.6-4.4 2.5-7 2.5-6.1 0-10.9-4.7-10.9-10.7s4.8-10.9 10.9-10.9 10.7 4.8 10.7 10.9c0 2.6-0.9 5.1-2.5 7l0.4 0.5h1.4z" />
              </svg>
            </div>
          </div>
        )}
        {showSelectAll && (
          <div
            className="kn-select-all"
            role="checkbox"
            aria-checked={allFilteredSelected}
            tabIndex={0}
            onClick={toggleAll}
            onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleAll(); } }}
          >
            {!ItemRenderer && (
              <input
                type="checkbox"
                className="kn-checkbox"
                checked={allFilteredSelected}
                readOnly
              />
            )}
            <div>Select All</div>
          </div>
        )}
        <div className="kn-list">
          {displayEntries.length === 0 ? (
            <div className="kn-no-items">No items</div>
          ) : (
            displayEntries.map((entry) => {
              if (entry.type === 'group') {
                return (
                  <div key={`group-${entry.label}`} className="kn-group-header">
                    {ItemRenderer ? (
                      <ItemRenderer item={entry.item} group={true} checked={false} />
                    ) : (
                      <span>{entry.label}</span>
                    )}
                  </div>
                );
              }
              const { item } = entry;
              const isChecked = selectedIds.has(item.id);
              const isDisabled = !!item.disabled;
              return (
                <div
                  key={item.id}
                  role="checkbox"
                  aria-checked={isChecked}
                  aria-disabled={isDisabled || undefined}
                  tabIndex={isDisabled ? -1 : 0}
                  className={`kn-item${isChecked ? ' kn-item-selected' : ''}${isDisabled ? ' kn-item-disabled' : ''}`}
                  style={{ height: itemHeight }}
                  onClick={() => toggleItem(item)}
                  onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleItem(item); } }}
                  title={item.label}
                >
                  {ItemRenderer ? (
                    <ItemRenderer item={item} group={false} checked={isChecked} />
                  ) : (
                    <>
                      <input
                        type="checkbox"
                        className="kn-checkbox"
                        checked={isChecked}
                        disabled={isDisabled}
                        readOnly
                      />
                      <div className="kn-item-label">{item.label}</div>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Right panel - selected items */}
      {showSelectedItems && (
        <div className="kn-column">
          <div className="kn-selection-status">
            <span className="kn-selection-status-text">{selectedItems.length} selected</span>
            {selectedItems.length > 0 && (
              <button type="button" className="kn-clear-all" onClick={clearAll}>
                Clear All
              </button>
            )}
          </div>
          <div className="kn-list">
            {selectedItems.length === 0 ? (
              <div className="kn-no-items">No Items...</div>
            ) : (
              selectedItems.map((item) => (
                <div key={item.id} className="kn-selected-item" style={{ height: itemHeight }}>
                  <div className="kn-item-label">{item.label}</div>
                  <button
                    type="button"
                    className="kn-remove-btn"
                    onClick={() => removeItem(item)}
                    aria-label={`Remove ${item.label}`}
                  >
                    &#x2715;
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

CheckboxMultiSelect.propTypes = {
  items: PropTypes.array,
  selectedItems: PropTypes.array,
  onChange: PropTypes.func,
  withGrouping: PropTypes.bool,
  itemRenderer: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  itemHeight: PropTypes.number,
  showSearch: PropTypes.bool,
  showSelectAll: PropTypes.bool,
  showSelectedItems: PropTypes.bool,
};

export default CheckboxMultiSelect;
